// src/pages/Teams.js
import React, { useEffect, useState, useContext, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import './Teams.css';
import '../styles/okr-m3-tokens.css';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import { AuthContext } from '../context/AuthContext';
import { calculateOKRStatus } from '../utils/okrStatusUtils';
import MetricsBar from '../components/okr/MetricsBar';
import StatusFilterChips from '../components/okr/StatusFilterChips';
import ObjectiveCard from '../components/okr/ObjectiveCard';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

export default function Teams() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  const [teams, setTeams]             = useState([]);
  const [okrs, setOkrs]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [expandedMap, setExpandedMap] = useState({});
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [expandedTeams, setExpandedTeams] = useState({});

  // Modal state
  const [showModal, setShowModal]   = useState(false);
  const [editingId, setEditingId]   = useState(null);
  const [teamName, setTeamName]     = useState('');

  const toggleExpand = id => setExpandedMap(m => ({ ...m, [id]: !m[id] }));
  
  const expandAll = (okrsList) => {
    setExpandedMap(prev => {
      const newMap = { ...prev };
      okrsList.forEach(o => { newMap[o._id] = true; });
      return newMap;
    });
  };
  
  const collapseAll = (okrsList) => {
    setExpandedMap(prev => {
      const newMap = { ...prev };
      okrsList.forEach(o => { newMap[o._id] = false; });
      return newMap;
    });
  };
  
  const handleToggleStatus = (status) => {
    setSelectedStatuses(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };
  
  const toggleTeamExpand = (teamId) => {
    setExpandedTeams(prev => ({ ...prev, [teamId]: !prev[teamId] }));
  };
  
  const getTeamInitials = (teamName) => {
    if (!teamName) return '?';
    const words = teamName.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    }
    return teamName.substring(0, 2).toUpperCase();
  };
  
  // Calculate overall stats
  const { overallProgress, overallScore, totalOkrs, totalKRs } = useMemo(() => {
    const totalOkrs = okrs.length;
    let totalKRs = 0, done = 0, sum = 0;
    okrs.forEach(o => {
      totalKRs += o.keyResults?.length || 0;
      done += o.keyResults?.filter(kr => kr.completed).length || 0;
      sum += o.score || 0;
    });
    const overallProgress = totalKRs ? Math.round((done / totalKRs) * 100) : 0;
    const overallScore = totalOkrs ? sum / totalOkrs : 0;
    return { overallProgress, overallScore, totalOkrs, totalKRs };
  }, [okrs]);

  // fetch teams + OKRs
  const fetchData = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const [tRes, oRes] = await Promise.all([
        apiGet('/api/teams', token),
        apiGet('/api/okrs',   token)
      ]);

      if (!tRes.ok) {
        const errorText = await tRes.text().catch(() => 'Unknown error');
        throw new Error(`Teams fetch failed (${tRes.status}): ${errorText}`);
      }
      if (!oRes.ok) {
        const errorText = await oRes.text().catch(() => 'Unknown error');
        throw new Error(`OKRs fetch failed (${oRes.status}): ${errorText}`);
      }

      const tBody = await tRes.json();
      const oBody = await oRes.json();

      const teamsArray = Array.isArray(tBody)
        ? tBody
        : Array.isArray(tBody.teams)
          ? tBody.teams
          : [];

      const okrsArray = Array.isArray(oBody)
        ? oBody
        : Array.isArray(oBody.okrs)
          ? oBody.okrs
          : [];

      setTeams(teamsArray);
      setOkrs(okrsArray);

      // init accordion state for OKRs
      const okrInit = {};
      okrsArray.forEach(o => { okrInit[o._id] = false; });
      setExpandedMap(okrInit);
      
      // Initialize team expansion (first 7 expanded by default)
      const teamInit = {};
      teamsArray.forEach((t, idx) => {
        const theirOKRs = okrsArray.filter(o =>
          o.keyResults && o.keyResults.some(kr =>
            Array.isArray(kr.team)
              ? kr.team.includes(t.name)
              : kr.team === t.name
          )
        );
        teamInit[t._id] = theirOKRs.length > 0 && idx < 7;
      });
      setExpandedTeams(teamInit);

    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to load data');
      setTeams([]);
      setOkrs([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchData();
  }, [fetchData, token]);

  const handleDelete = async id => {
    if (!window.confirm('Delete this team?')) return;
    try {
      const res = await apiDelete(`/api/teams/${id}`, token);
      if (!res.ok) {
        let msg = `Delete failed (${res.status})`;
        try {
          const body = await res.json();
          msg = body.error || msg;
        } catch {}
        throw new Error(msg);
      }
      await fetchData();
    } catch (err) {
      alert(err.message);
    }
  };

  const openAdd  = () => {
    setEditingId(null);
    setTeamName('');
    setShowModal(true);
  };
  const openEdit = team => {
    setEditingId(team._id);
    setTeamName(team.name);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!teamName.trim()) {
      return alert('Please enter a team name.');
    }

    // --- Edit existing team ---
    if (editingId) {
      try {
        const res = await apiPut(
          `/api/teams/${editingId}`,
          token,
          { name: teamName.trim() }
        );
        if (!res.ok) {
          let msg = `Save failed (${res.status})`;
          try {
            const body = await res.json();
            msg = body.error || msg;
          } catch {}
          throw new Error(msg);
        }
        // reload full list from server
        await fetchData();
      } catch (err) {
        console.warn('PUT failed, falling back locally:', err.message);
        // immediate UI update even if backend PUT fails
        setTeams(ts =>
          ts.map(t =>
            t._id === editingId ? { ...t, name: teamName.trim() } : t
          )
        );
      }
    }
    // --- Add new team ---
    else {
      try {
        const res = await apiPost(
          `/api/teams`,
          token,
          { name: teamName.trim() }
        );
        if (!res.ok) {
          let msg = `Create failed (${res.status})`;
          try {
            const body = await res.json();
            msg = body.error || msg;
          } catch {}
          throw new Error(msg);
        }
        await fetchData();
      } catch (err) {
        alert(err.message);
        return;
      }
    }

    setShowModal(false);
    setEditingId(null);
    setTeamName('');
  };

  const handleToggleKR = async (okrId, krIdx) => {
    const target = okrs.find(o => o._id === okrId);
    if (!target || !target.keyResults || !target.keyResults[krIdx]) {
      console.error('❌ [Teams] Invalid OKR or key result index:', { okrId, krIdx, target: !!target });
      return;
    }
    
    // Toggle the completed status
    const currentCompleted = target.keyResults[krIdx].completed || false;
    const newCompleted = !currentCompleted;
    
    console.log('🔄 [Teams] Toggling KR:', { 
      okrId, 
      krIdx, 
      current: currentCompleted, 
      new: newCompleted,
      krText: target.keyResults[krIdx].text?.substring(0, 30)
    });
    
    // Create new keyResults array with updated completed status
    const newKRs = target.keyResults.map((kr, i) => {
      if (i === krIdx) {
        return {
          text: kr.text || '',
          team: kr.team || undefined,
          krOwner: kr.krOwner || undefined,
          completed: newCompleted, // Explicit boolean
          deadline: kr.deadline || undefined
        };
      }
      return {
        text: kr.text || '',
        team: kr.team || undefined,
        krOwner: kr.krOwner || undefined,
        completed: kr.completed === true || kr.completed === 'true' || false, // Ensure boolean
        deadline: kr.deadline || undefined
      };
    });
    
    const done = newKRs.filter(kr => kr.completed === true).length;
    const total = newKRs.length;
    const progress = total ? Math.round((done / total) * 100) : 0;
    const status = calculateOKRStatus(newKRs, target.deadline);
    
    // Prepare payload - only send necessary fields
    const payload = {
      objective: target.objective,
      deadline: target.deadline,
      level: target.level,
      owners: target.owners || [target.owner].filter(Boolean),
      owner: target.owner,
      year: target.year,
      quarter: target.quarter,
      type: target.type,
      keyResults: newKRs,
      progress,
      score: target.score || 0,
      status
    };
    
    console.log('📤 [Teams] Sending payload:', {
      okrId,
      level: payload.level,
      owners: payload.owners,
      year: payload.year,
      quarter: payload.quarter,
      type: payload.type,
      keyResultsCount: payload.keyResults.length,
      completedCount: payload.keyResults.filter(kr => kr.completed).length,
      progress: payload.progress,
      status: payload.status,
      keyResults: payload.keyResults.map((kr, i) => ({
        idx: i,
        text: kr.text?.substring(0, 30),
        completed: kr.completed,
        team: kr.team,
        krOwner: kr.krOwner
      }))
    });
    
    // Optimistically update UI
    setOkrs(prev => prev.map(o => o._id === okrId ? { ...target, ...payload } : o));
    
    try {
      const res = await apiPut(`/api/okrs/${okrId}`, token, payload);
      if (res.ok) {
        // Update with server response to ensure we have the saved data
        try {
          const savedOKR = await res.json();
          console.log('✅ [Teams] Saved OKR received:', {
            id: savedOKR._id,
            keyResults: savedOKR.keyResults?.map((kr, i) => ({
              idx: i,
              text: kr.text?.substring(0, 20),
              completed: kr.completed,
              type: typeof kr.completed
            }))
          });
          setOkrs(prev => prev.map(o => {
            if (o._id === okrId) {
              return savedOKR;
            }
            return o;
          }));
        } catch (parseError) {
          console.error('❌ [Teams] Error parsing saved OKR response:', parseError);
          // Keep the optimistic update if parsing fails
        }
      } else {
        // Revert on failure
        setOkrs(prev => prev.map(o => o._id === okrId ? target : o));
        let errorMessage = 'Unknown error';
        try {
          const errorData = await res.json();
          errorMessage = errorData.error || errorData.message || JSON.stringify(errorData);
          console.error('❌ [Teams] Failed to update OKR:', res.status, errorData);
        } catch (parseErr) {
          const errorText = await res.text().catch(() => 'Unknown error');
          errorMessage = errorText;
          console.error('❌ [Teams] Failed to update OKR (non-JSON):', res.status, errorText);
        }
        alert(`Failed to save changes: ${errorMessage}`);
      }
    } catch (err) {
      // Revert on error
      setOkrs(prev => prev.map(o => o._id === okrId ? target : o));
      console.error('❌ [Teams] Error updating OKR:', err);
      alert(`Failed to save changes: ${err.message || 'Network error. Please check your connection and try again.'}`);
    }
  };
  
  const handleEditOKR = (id) => {
    navigate(`/editokr/${id}`);
  };
  
  const handleDeleteOKR = async (id) => {
    if (!window.confirm('Are you sure you want to delete this OKR?')) return;
    try {
      const res = await apiDelete(`/api/okrs/${id}`, token);
      if (res.ok) {
        setOkrs(prev => prev.filter(o => o._id !== id));
      }
    } catch (err) {
      alert('Failed to delete OKR.');
    }
  };

  if (loading) return <div className="m3-center-message">Loading…</div>;
  if (error)   return <div className="m3-center-message m3-error">Error: {error}</div>;

  return (
    <div className="m3-teams-okrs-page">
      <div className="m3-page-header">
        <h1 className="m3-page-title">Team OKRs</h1>
        <p className="m3-page-subtitle">All OKRs grouped by team</p>
      </div>

      {/* Metrics Bar */}
      {okrs.length > 0 && (
        <MetricsBar
          progress={overallProgress}
          score={overallScore}
          objectives={totalOkrs}
          keyResults={totalKRs}
        />
      )}

      {/* Status Filter Chips */}
      <StatusFilterChips
        selectedStatuses={selectedStatuses}
        onToggleStatus={handleToggleStatus}
      />

      {/* Teams with OKRs */}
      <div className="m3-teams-list">
        {teams.map(teamObj => {
          const theirOKRs = okrs.filter(o => {
            const okrWithStatus = {
              ...o,
              status: calculateOKRStatus(o.keyResults || [], o.deadline)
            };
            
            // Filter by selected statuses if any
            if (selectedStatuses.length > 0 && !selectedStatuses.includes(okrWithStatus.status)) {
              return false;
            }
            
            return o.keyResults.some(kr =>
              Array.isArray(kr.team)
                ? kr.team.includes(teamObj.name)
                : kr.team === teamObj.name
            );
          });
          
          const isTeamExpanded = expandedTeams[teamObj._id] !== false;
          
          // Calculate team stats (handle teams with no OKRs)
          const onTrack = theirOKRs.length > 0 ? theirOKRs.filter(o => {
            const status = calculateOKRStatus(o.keyResults || [], o.deadline);
            return status === 'On Track';
          }).length : 0;
          const atRisk = theirOKRs.length > 0 ? theirOKRs.filter(o => {
            const status = calculateOKRStatus(o.keyResults || [], o.deadline);
            return status === 'At Risk';
          }).length : 0;
          const behind = theirOKRs.length > 0 ? theirOKRs.filter(o => {
            const status = calculateOKRStatus(o.keyResults || [], o.deadline);
            return status === 'Behind';
          }).length : 0;
          const teamStats = { onTrack, atRisk, behind, total: theirOKRs.length };
          
          return (
            <div key={teamObj._id} className="m3-team-card">
              <div className="m3-team-card-header" onClick={() => toggleTeamExpand(teamObj._id)}>
                <div className="m3-team-avatar">
                  {getTeamInitials(teamObj.name)}
                </div>
                <div className="m3-team-info">
                  <h2 className="m3-team-name">{teamObj.name}</h2>
                  <div className="m3-team-stats">
                    <span className="m3-team-stat-chip">Objectives: {teamStats.total}</span>
                    <span className="m3-team-stat-chip">On Track: {teamStats.onTrack}</span>
                    <span className="m3-team-stat-chip">At Risk: {teamStats.atRisk}</span>
                    <span className="m3-team-stat-chip">Behind: {teamStats.behind}</span>
                  </div>
                </div>
                <button className="m3-expand-team-button">
                  {isTeamExpanded ? <FaChevronUp /> : <FaChevronDown />}
                </button>
              </div>
              
              {isTeamExpanded && (
                <div className="m3-team-okrs">
                  {theirOKRs.length > 0 ? (
                    <>
                      <div className="m3-expand-controls">
                        <button className="m3-button m3-button-text" onClick={() => expandAll(theirOKRs)}>
                          Expand All
                        </button>
                        <button className="m3-button m3-button-text" onClick={() => collapseAll(theirOKRs)}>
                          Collapse All
                        </button>
                      </div>
                      <div className="m3-okrs-list">
                        {theirOKRs.map(okr => (
                          <ObjectiveCard
                            key={okr._id}
                            okr={{
                              ...okr,
                              status: calculateOKRStatus(okr.keyResults || [], okr.deadline)
                            }}
                            isExpanded={expandedMap[okr._id] || false}
                            onToggleExpand={toggleExpand}
                            onToggleKR={handleToggleKR}
                            onEdit={handleEditOKR}
                            onDelete={handleDeleteOKR}
                            canEdit={true}
                            canDelete={true}
                          />
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="m3-empty-team-okrs">
                      <p>No OKRs for this team.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
